import React, { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Calculator, TrendingUp, TrendingDown, DollarSign, Percent, Info } from 'lucide-react';

const BaseballEVCalculator = ({ prefilledGame }) => {
  const [betType, setBetType] = useState('moneyline');
  const [odds, setOdds] = useState('');
  const [impliedProb, setImpliedProb] = useState('');
  const [actualProb, setActualProb] = useState('');
  const [betAmount, setBetAmount] = useState('100');
  const [useImpliedProb, setUseImpliedProb] = useState(true);
  const [selectedBookmaker, setSelectedBookmaker] = useState(null);
  
  // Team selection for moneyline/runline
  const [selectedTeam, setSelectedTeam] = useState('');
  
  // Run line specific
  const [runLine, setRunLine] = useState('-1.5');
  const [runLineOdds, setRunLineOdds] = useState('');
  
  // Over/Under specific
  const [totalLine, setTotalLine] = useState('9');
  const [overUnderChoice, setOverUnderChoice] = useState('over');
  const [totalOdds, setTotalOdds] = useState('');
  
  // Extract available bookmakers and odds from game data
  const availableOdds = useMemo(() => {
    if (!prefilledGame?.oddsData) return null;
    
    // Convert array to object keyed by bookmaker id for easier access
    const bookmakers = {};
    prefilledGame.oddsData.forEach(bookmaker => {
      bookmakers[bookmaker.id] = bookmaker;
    });
    
    return bookmakers;
  }, [prefilledGame]);
  
  // Auto-fill odds when bookmaker and team are selected
  React.useEffect(() => {
    if (selectedBookmaker && selectedTeam && availableOdds) {
      const bookmaker = availableOdds[selectedBookmaker];
      if (bookmaker) {
        // Find the moneyline bet (id: 1 is "Home/Away" which is moneyline)
        const moneylineBet = bookmaker.bets.find(bet => bet.id === 1);
        if (moneylineBet && moneylineBet.values) {
          // Find the odds for the selected team
          const teamOdds = moneylineBet.values.find(value => 
            (selectedTeam === 'home' && value.value === 'Home') ||
            (selectedTeam === 'away' && value.value === 'Away')
          );
          
          if (teamOdds && teamOdds.odd) {
            // Convert decimal odds to American odds
            const decimal = parseFloat(teamOdds.odd);
            let american;
            if (decimal >= 2) {
              american = `+${Math.round((decimal - 1) * 100)}`;
            } else {
              american = `-${Math.round(100 / (decimal - 1))}`;
            }
            setOdds(american);
          }
        }
      }
    }
  }, [selectedBookmaker, selectedTeam, availableOdds]);

  const betTypes = [
    { id: 'moneyline', label: 'Moneyline', icon: DollarSign },
    { id: 'runline', label: 'Run Line', icon: TrendingUp },
    { id: 'total', label: 'Over/Under', icon: Calculator },
    { id: 'parlay', label: 'Parlay', icon: Percent }
  ];

  const convertAmericanToDecimal = useCallback((american) => {
    const num = parseFloat(american);
    if (isNaN(num)) return null;
    
    if (num > 0) {
      return (num / 100) + 1;
    } else {
      return (100 / Math.abs(num)) + 1;
    }
  }, []);

  const convertDecimalToAmerican = useCallback((decimal) => {
    if (decimal >= 2) {
      return `+${Math.round((decimal - 1) * 100)}`;
    } else {
      return `-${Math.round(100 / (decimal - 1))}`;
    }
  }, []);

  const calculateImpliedProbability = useCallback((american) => {
    const num = parseFloat(american);
    if (isNaN(num)) return 0;
    
    if (num > 0) {
      return 100 / (num + 100) * 100;
    } else {
      return Math.abs(num) / (Math.abs(num) + 100) * 100;
    }
  }, []);

  const calculatePayout = useCallback((american, stake) => {
    const num = parseFloat(american);
    const betSize = parseFloat(stake);
    if (isNaN(num) || isNaN(betSize)) return 0;
    
    if (num > 0) {
      return betSize * (num / 100);
    } else {
      return betSize * (100 / Math.abs(num));
    }
  }, []);

  const calculateEV = useCallback(() => {
    let probability = 0;
    let payout = 0;
    const stake = parseFloat(betAmount) || 0;

    if (betType === 'moneyline') {
      const oddsValue = parseFloat(odds);
      if (isNaN(oddsValue)) return { ev: 0, roi: 0 };
      
      probability = useImpliedProb 
        ? calculateImpliedProbability(odds) / 100
        : parseFloat(actualProb) / 100 || 0;
      
      payout = calculatePayout(odds, stake);
    } else if (betType === 'runline') {
      const oddsValue = parseFloat(runLineOdds);
      if (isNaN(oddsValue)) return { ev: 0, roi: 0 };
      
      probability = parseFloat(actualProb) / 100 || 0;
      payout = calculatePayout(runLineOdds, stake);
    } else if (betType === 'total') {
      const oddsValue = parseFloat(totalOdds);
      if (isNaN(oddsValue)) return { ev: 0, roi: 0 };
      
      probability = parseFloat(actualProb) / 100 || 0;
      payout = calculatePayout(totalOdds, stake);
    }

    const ev = (probability * payout) - ((1 - probability) * stake);
    const roi = stake > 0 ? (ev / stake) * 100 : 0;

    return { ev, roi };
  }, [betType, odds, impliedProb, actualProb, betAmount, useImpliedProb, 
      calculateImpliedProbability, calculatePayout, runLineOdds, totalOdds]);

  const { ev, roi } = useMemo(() => calculateEV(), [calculateEV]);

  const handleOddsChange = useCallback((e) => {
    const value = e.target.value;
    setOdds(value);
    if (value) {
      const implied = calculateImpliedProbability(value);
      setImpliedProb(implied.toFixed(2));
    } else {
      setImpliedProb('');
    }
  }, [calculateImpliedProbability]);

  const parlayLegs = useState([
    { id: 1, description: '', odds: '', probability: '' }
  ]);

  const addParlayLeg = useCallback(() => {
    parlayLegs[1](prev => [...prev, { 
      id: Date.now(), 
      description: '', 
      odds: '', 
      probability: '' 
    }]);
  }, [parlayLegs]);

  const removeParlayLeg = useCallback((id) => {
    parlayLegs[1](prev => prev.filter(leg => leg.id !== id));
  }, [parlayLegs]);

  const updateParlayLeg = useCallback((id, field, value) => {
    parlayLegs[1](prev => prev.map(leg => 
      leg.id === id ? { ...leg, [field]: value } : leg
    ));
  }, [parlayLegs]);

  const calculateParlayEV = useCallback(() => {
    const legs = parlayLegs[0];
    const stake = parseFloat(betAmount) || 0;
    
    let totalOdds = 1;
    let totalProb = 1;
    
    for (const leg of legs) {
      if (leg.odds && leg.probability) {
        const decimal = convertAmericanToDecimal(leg.odds);
        if (decimal) {
          totalOdds *= decimal;
          totalProb *= parseFloat(leg.probability) / 100;
        }
      }
    }
    
    const payout = stake * (totalOdds - 1);
    const ev = (totalProb * payout) - ((1 - totalProb) * stake);
    const roi = stake > 0 ? (ev / stake) * 100 : 0;
    
    return { ev, roi, totalOdds: convertDecimalToAmerican(totalOdds), totalProb: totalProb * 100 };
  }, [parlayLegs, betAmount, convertAmericanToDecimal, convertDecimalToAmerican]);

  const parlayResults = useMemo(() => 
    betType === 'parlay' ? calculateParlayEV() : null, 
    [betType, calculateParlayEV]
  );

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
      <div className="flex items-center gap-3 mb-6">
        <Calculator className="w-6 h-6 text-blue-400" />
        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
          Baseball EV Calculator
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
        {betTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => setBetType(type.id)}
            className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
              betType === type.id
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                : 'bg-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <type.icon className="w-4 h-4" />
            <span className="text-sm font-medium">{type.label}</span>
          </button>
        ))}
      </div>

      {prefilledGame && (
        <div className={`border rounded-lg p-4 mb-4 ${
          availableOdds ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'
        }`}>
          <div className="flex items-start gap-3">
            <Info className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              availableOdds ? 'text-green-500' : 'text-yellow-500'
            }`} />
            <div>
              <p className={`text-sm ${availableOdds ? 'text-green-200' : 'text-yellow-200'}`}>
                {availableOdds 
                  ? `Live odds available from ${Object.keys(availableOdds).length} bookmaker(s). Select a bookmaker to auto-fill odds.`
                  : 'Live odds data is not currently available. Please enter the odds manually from your preferred sportsbook.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {betType === 'moneyline' && (
        <div className="space-y-4">
          {availableOdds && (
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Select Bookmaker
              </label>
              <select
                value={selectedBookmaker || ''}
                onChange={(e) => setSelectedBookmaker(e.target.value)}
                className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="">Choose a bookmaker...</option>
                {Object.entries(availableOdds).map(([id, data]) => (
                  <option key={id} value={id}>{data.name}</option>
                ))}
              </select>
            </div>
          )}
          
          {prefilledGame && (
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Select Team
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setSelectedTeam('away')}
                  className={`p-3 rounded-lg border transition-all ${
                    selectedTeam === 'away'
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-gray-700 bg-gray-900/50 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {prefilledGame.teams?.away?.logo && (
                      <img 
                        src={prefilledGame.teams.away.logo} 
                        alt={prefilledGame.teams.away.name}
                        className="w-8 h-8 object-contain"
                      />
                    )}
                    <span className="font-medium">{prefilledGame.teams?.away?.name}</span>
                  </div>
                </button>
                <button
                  onClick={() => setSelectedTeam('home')}
                  className={`p-3 rounded-lg border transition-all ${
                    selectedTeam === 'home'
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-gray-700 bg-gray-900/50 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {prefilledGame.teams?.home?.logo && (
                      <img 
                        src={prefilledGame.teams.home.logo} 
                        alt={prefilledGame.teams.home.name}
                        className="w-8 h-8 object-contain"
                      />
                    )}
                    <span className="font-medium">{prefilledGame.teams?.home?.name}</span>
                  </div>
                </button>
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              American Odds {selectedTeam && prefilledGame ? `for ${prefilledGame.teams?.[selectedTeam]?.name}` : ''}
            </label>
            <input
              type="text"
              value={odds}
              onChange={handleOddsChange}
              placeholder="-150 or +200"
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Implied Probability
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={impliedProb}
                  readOnly
                  className="w-full px-4 py-2 bg-gray-900/30 border border-gray-700 rounded-lg text-gray-400"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Your Probability
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={actualProb}
                  onChange={(e) => setActualProb(e.target.value)}
                  placeholder="55"
                  disabled={useImpliedProb}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 disabled:opacity-50"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="useImplied"
              checked={useImpliedProb}
              onChange={(e) => setUseImpliedProb(e.target.checked)}
              className="w-4 h-4 rounded border-gray-700 bg-gray-900 text-blue-500 focus:ring-blue-500"
            />
            <label htmlFor="useImplied" className="text-sm text-gray-400">
              Use implied probability for calculations
            </label>
          </div>
        </div>
      )}

      {betType === 'runline' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Run Line
              </label>
              <select
                value={runLine}
                onChange={(e) => setRunLine(e.target.value)}
                className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="-2.5">-2.5</option>
                <option value="-1.5">-1.5</option>
                <option value="-0.5">-0.5</option>
                <option value="+0.5">+0.5</option>
                <option value="+1.5">+1.5</option>
                <option value="+2.5">+2.5</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Odds
              </label>
              <input
                type="text"
                value={runLineOdds}
                onChange={(e) => setRunLineOdds(e.target.value)}
                placeholder="-110"
                className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Probability of Covering
            </label>
            <div className="relative">
              <input
                type="text"
                value={actualProb}
                onChange={(e) => setActualProb(e.target.value)}
                placeholder="55"
                className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
            </div>
          </div>
        </div>
      )}

      {betType === 'total' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Total Line
              </label>
              <input
                type="text"
                value={totalLine}
                onChange={(e) => setTotalLine(e.target.value)}
                placeholder="9"
                className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Selection
              </label>
              <select
                value={overUnderChoice}
                onChange={(e) => setOverUnderChoice(e.target.value)}
                className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="over">Over</option>
                <option value="under">Under</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Odds
              </label>
              <input
                type="text"
                value={totalOdds}
                onChange={(e) => setTotalOdds(e.target.value)}
                placeholder="-110"
                className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Probability
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={actualProb}
                  onChange={(e) => setActualProb(e.target.value)}
                  placeholder="55"
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {betType === 'parlay' && (
        <div className="space-y-4">
          {parlayLegs[0].map((leg, index) => (
            <motion.div
              key={leg.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-gray-900/30 rounded-lg p-4 border border-gray-700/50"
            >
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-medium text-gray-400">Leg {index + 1}</span>
                {parlayLegs[0].length > 1 && (
                  <button
                    onClick={() => removeParlayLeg(leg.id)}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={leg.description}
                  onChange={(e) => updateParlayLeg(leg.id, 'description', e.target.value)}
                  placeholder="Bet description"
                  className="px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm"
                />
                <input
                  type="text"
                  value={leg.odds}
                  onChange={(e) => updateParlayLeg(leg.id, 'odds', e.target.value)}
                  placeholder="Odds (-110)"
                  className="px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm"
                />
                <div className="relative">
                  <input
                    type="text"
                    value={leg.probability}
                    onChange={(e) => updateParlayLeg(leg.id, 'probability', e.target.value)}
                    placeholder="Win % (55)"
                    className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                </div>
              </div>
            </motion.div>
          ))}
          
          <button
            onClick={addParlayLeg}
            className="w-full py-2 border border-dashed border-gray-700 rounded-lg text-gray-400 hover:text-white hover:border-gray-600 transition-colors"
          >
            + Add Leg
          </button>

          {parlayResults && (
            <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-700/50">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Combined Odds:</span>
                  <span className="ml-2 text-white font-medium">{parlayResults.totalOdds}</span>
                </div>
                <div>
                  <span className="text-gray-500">Hit Probability:</span>
                  <span className="ml-2 text-white font-medium">{parlayResults.totalProb.toFixed(2)}%</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-gray-700/50">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Bet Amount
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
            <input
              type="text"
              value={betAmount}
              onChange={(e) => setBetAmount(e.target.value)}
              placeholder="100"
              className="w-full pl-8 pr-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className={`bg-gradient-to-r ${
              (betType === 'parlay' ? parlayResults?.ev : ev) >= 0
                ? 'from-green-500/20 to-green-600/20 border-green-500/50'
                : 'from-red-500/20 to-red-600/20 border-red-500/50'
            } rounded-lg p-4 border`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Expected Value</span>
              {(betType === 'parlay' ? parlayResults?.ev : ev) >= 0 ? (
                <TrendingUp className="w-5 h-5 text-green-400" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-400" />
              )}
            </div>
            <p className={`text-2xl font-bold mt-1 ${
              (betType === 'parlay' ? parlayResults?.ev : ev) >= 0 ? 'text-green-400' : 'text-red-400'
            }`}>
              ${(betType === 'parlay' ? parlayResults?.ev : ev)?.toFixed(2) || '0.00'}
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className={`bg-gradient-to-r ${
              (betType === 'parlay' ? parlayResults?.roi : roi) >= 0
                ? 'from-blue-500/20 to-purple-600/20 border-blue-500/50'
                : 'from-orange-500/20 to-red-600/20 border-orange-500/50'
            } rounded-lg p-4 border`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">ROI</span>
              <Percent className="w-5 h-5 text-blue-400" />
            </div>
            <p className={`text-2xl font-bold mt-1 ${
              (betType === 'parlay' ? parlayResults?.roi : roi) >= 0 ? 'text-blue-400' : 'text-orange-400'
            }`}>
              {(betType === 'parlay' ? parlayResults?.roi : roi)?.toFixed(2) || '0.00'}%
            </p>
          </motion.div>
        </div>

        <div className="mt-4 p-4 bg-gray-900/30 rounded-lg border border-gray-700/50">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-400 mt-0.5" />
            <div className="text-sm text-gray-400">
              <p className="font-medium text-gray-300 mb-1">How to use:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Enter the odds offered by your sportsbook</li>
                <li>Input your estimated probability of the bet winning</li>
                <li>Positive EV (+) indicates a profitable bet long-term</li>
                <li>ROI shows your expected return per dollar wagered</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseballEVCalculator;